import { APIReferenceAttributes, ArdoqId } from '@ardoq/api-types';
import {
  getFillAndStroke,
  sanitizeFilterColorClassName,
} from '@ardoq/color-helpers';
import {
  type EnhancedScopeData,
  GetCssClassNamesOption,
} from '@ardoq/data-model';
import { enhancedScopeDataOperations } from './enhancedScopeDataOperations';
import type { FilterColorData } from './filterColorDataOperations';
import { filterColorDataOperations } from './filterColorDataOperations';
import { resolveFilterColor } from './filterColorUtils';

export const toGlobalReferenceTypeId = (
  reference: Pick<APIReferenceAttributes, 'rootWorkspace' | 'type'>
): string => `${reference.rootWorkspace}-${reference.type}`;

export const getGlobalTypeId = (
  scopeData: EnhancedScopeData,
  referenceId: ArdoqId
) => {
  const reference = scopeData.referencesById[referenceId];
  if (!reference) return null;
  return toGlobalReferenceTypeId(reference);
};

export const getWorkspaceId = (
  scopeData: EnhancedScopeData,
  referenceId: ArdoqId
): ArdoqId | null => {
  const reference = scopeData.referencesById[referenceId];
  if (!reference) return null;
  return reference.rootWorkspace;
};

/**
 * @deprecated Use enhancedScopeDataOperations.getReferenceTypeByReferenceId instead
 */
export const getTypeOfReference =
  enhancedScopeDataOperations.getReferenceTypeByReferenceId;

export const getAttribute = (
  enhancedScopeData: EnhancedScopeData,
  referenceId: ArdoqId,
  fieldName: string
) => {
  const reference = enhancedScopeDataOperations.getReference(
    enhancedScopeData,
    referenceId
  );
  return reference ? reference[fieldName] : null;
};

export const isReference = (
  enhancedScopeData: EnhancedScopeData,
  referenceId: ArdoqId
) => {
  const reference = enhancedScopeDataOperations.getReference(
    enhancedScopeData,
    referenceId
  );
  return reference?.ardoq['entity-type'] === 'reference';
};

const getReferenceFilterColorClassName = (
  filterColorData: FilterColorData,
  referenceId: ArdoqId
): string => {
  const entry = filterColorDataOperations.getReferenceFilterColor(
    filterColorData,
    referenceId
  );
  if (!entry) return '';
  return sanitizeFilterColorClassName(entry.color);
};

const getReferenceTypeColorAsSVGAttributes = (
  scopeData: EnhancedScopeData,
  referenceId: ArdoqId,
  { useAsBackgroundStyle }: GetCssClassNamesOption
): { fill?: string; stroke?: string } => {
  const reference = scopeData.referencesById[referenceId];
  if (!reference) return { fill: undefined, stroke: undefined };
  const referenceType =
    enhancedScopeDataOperations.getReferenceTypeByReferenceId(
      scopeData,
      referenceId
    );
  if (!referenceType) return { fill: undefined, stroke: undefined };
  return getFillAndStroke(referenceType.color, useAsBackgroundStyle);
};

/**
 * Pure replacement for referenceInterface.getCssColors.
 *
 * Returns fill/stroke SVG attributes for a reference, accounting for conditional
 * formatting. Falls back to the reference type color if no filter color is set.
 */
export const getReferenceDisplayColorAsSVGAttributes = (
  scopeData: EnhancedScopeData,
  filterColorData: FilterColorData,
  referenceId: ArdoqId,
  options: GetCssClassNamesOption
): { fill?: string; stroke?: string } => {
  const filterEntry = filterColorDataOperations.getReferenceFilterColor(
    filterColorData,
    referenceId
  );
  if (filterEntry) {
    return resolveFilterColor(filterEntry.color, filterEntry.version, options);
  }
  return getReferenceTypeColorAsSVGAttributes(scopeData, referenceId, options);
};

/**
 * Pure replacement for referenceInterface.getCssClassNames.
 *
 * Returns a CSS class string for a reference, including a filter color class
 * when conditional formatting is active.
 */
export const getReferenceCSSClassNames = (
  scopeData: EnhancedScopeData,
  filterColorData: FilterColorData,
  referenceId: ArdoqId
): string | null => {
  const reference = scopeData.referencesById[referenceId];
  if (!reference) return null;
  const filterClass = getReferenceFilterColorClassName(
    filterColorData,
    referenceId
  );
  return `integration m${reference.model} nr${reference.type} id_${reference._id} ${filterClass}`.trim();
};
